import { useAtomValue } from "jotai";
import { useWallet } from "#/hooks/use-wallet";
import { useWalletBalance } from "#/hooks/use-wallet-balance";
import {
	pendingWithdrawalInTransit,
	pendingWithdrawalJustCompleted,
	pendingWithdrawalsAtom,
} from "../stores/pending-withdrawal";

/**
 * True while a "transfer to wallet" withdrawal leg is in-flight (~5 min) for
 * the connected address — the same derivation the withdraw dialog uses.
 * Mirror of `use-deposit-in-transit.ts`, watching the on-chain wallet balance
 * (30s poll) instead of the trading balance, so arrival detection can lag up
 * to ~30s — same granularity the dialog already has. Readers stay timer-free:
 * the record lifecycle (and its one timer) lives in withdraw-dialog.
 */
export function useWithdrawalInTransit(): boolean {
	const { address } = useWallet();
	const pendingMap = useAtomValue(pendingWithdrawalsAtom);
	const { balance: walletBalance } = useWalletBalance();
	const record = address ? pendingMap[address.toLowerCase()] : undefined;
	return pendingWithdrawalInTransit(record, walletBalance, Date.now());
}

/**
 * True while the post-arrival "completed" check should show (a short window
 * after the funds land in the wallet). Timer-free like the hook above:
 * withdraw-dialog's lifecycle effect deletes the record when the window ends,
 * and that atom write re-renders every reader.
 */
export function useWithdrawalJustCompleted(): boolean {
	const { address } = useWallet();
	const pendingMap = useAtomValue(pendingWithdrawalsAtom);
	const record = address ? pendingMap[address.toLowerCase()] : undefined;
	return pendingWithdrawalJustCompleted(record, Date.now());
}
